import { type Ref, memo, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import classNames from "classnames";
import { cloneDeep } from "lodash";
import { FCI_ERROR } from "@searchui-searchforms/shared/fci";
import { invariant } from "@searchui-searchforms/shared/utils";
import {
    Form,
    type FormValues,
    RELATION_DEFAULT_INSTANCE,
    type SubmitMeta,
    createEventDefaultInstance,
} from "@/widgets/classic-form/form";
import { useOnAddField, useOnRemoveField } from "@/features/form-fields";
import { useMetadataQuery } from "@/entities/form-metadata";
import { LOCATION_EXACTNESS_SCOPE } from "@/entities/place";
import type { FieldPath, FormAPI } from "@/shared/forms";
import { useLogFCI } from "@/shared/lib/fci";
import { usePageContextQuery } from "@/shared/page-context";
import "@/shared/styles/globals.scss";
import { useUICustomization } from "@/shared/ui-customization";
import { FullFormStructure } from "./FullFormStructure";
import { IsEditFormProvider } from "./IsEditFormContext";
import { applyPersonToForm } from "./field-group-renderer/NameGroup/quick-fill/applyPersonToForm";
import { useDefaultFormValues } from "./form-control/useDefaultFormValues";
import { useOnSubmit } from "./form-control/useOnSubmit";
import { useRestoreFormHistoryState } from "./form-history";
import { MatchAllTermsProvider, type MatchAllTermsProviderRef } from "./match-all-terms";
import { useSearchAssist } from "./search-assist";
import { useResetFormOnUpstreamChange } from "./useResetFormOnUpstreamChange";
import styles from "./ClassicForm.module.scss";

const FORM_SIZE_MAP = {
    large: "formLarge",
    largest: "formLargest",
} as const;

const MemoizedFullFormStructure = memo(FullFormStructure);

export interface ClassicFormHandle {
    /**
     * Applies a tree person to the mounted form as a DRAFT (same as a manual
     * quick-fill selection): the form becomes dirty, nothing is submitted and
     * the committed dataModel is untouched.
     */
    applyPerson: (pick: { personId: string; treeId: string }) => Promise<void>;
}

interface ClassicFormProps {
    isEditForm?: boolean;
    /** Narrow imperative handle for external orchestration (e.g. slider quick-fill pick). */
    imperativeRef?: Ref<ClassicFormHandle>;
}

export const ClassicForm = ({ isEditForm = false, imperativeRef }: ClassicFormProps) => {
    const [hydrated, setHydrated] = useState(false);
    const logFCI = useLogFCI();
    useEffect(() => {
        setHydrated(true);
    }, []);

    const { formInputsSize } = useUICustomization();

    const defaultFormValues = useDefaultFormValues({ isEditForm });
    // We expect creating initial form values only once
    // Also with deferred loading at this point
    // we are guaranteed to get all the data from the queries
    //
    // Redundant since ADR-016: the adapter treats `initialValues` as a mount-time seed
    // and ignores later changes to it. Kept for now so removing it is its own change.
    const initialFormValuesRef = useRef(defaultFormValues);

    const [getShouldOpenSearchAssist, openSearchAssist] = useSearchAssist();

    const formRef = useRef<FormAPI<FormValues, SubmitMeta>>(null);
    const matchAllTermsRef = useRef<MatchAllTermsProviderRef>(null);

    const handleSubmit = useOnSubmit({ isEditForm });

    useRestoreFormHistoryState({
        enabled: !isEditForm,
        setValues: (values) => formRef.current?.setValues(values),
    });

    const onSubmit = useCallback(
        async (values: FormValues, meta?: SubmitMeta) => {
            try {
                if (!isEditForm && getShouldOpenSearchAssist(values)) {
                    openSearchAssist();
                    return;
                }
                const matchAllTermsExactly = matchAllTermsRef.current?.matchAllTermsExactly ?? false;
                const preferences: { matchAllTermsExactly: boolean; isSkipAssistPlacesModalPermanently?: boolean } = {
                    matchAllTermsExactly,
                };
                if (meta?.dontShowSearchAssistAgain) {
                    preferences.isSkipAssistPlacesModalPermanently = true;
                }
                await handleSubmit(values, preferences);
            } catch (error) {
                logFCI({
                    errorType: FCI_ERROR.Navigation,
                    errorInfo: `Classic form submit failed: ${String(error)}`,
                });
            }
        },
        [isEditForm, getShouldOpenSearchAssist, handleSubmit, openSearchAssist, logFCI],
    );

    useResetFormOnUpstreamChange(formRef, defaultFormValues);

    const queryClient = useQueryClient();
    const { data: formMetadata } = useMetadataQuery();
    const { data: pageContext } = usePageContextQuery();

    // Narrow imperative handle: exactly one capability, applyPerson. The child
    // <Form>'s formRef attaches before this handle does (child refs attach
    // before parent effects), so formRef.current is set by the time a caller
    // can invoke it.
    useImperativeHandle(
        imperativeRef,
        () => ({
            applyPerson: async ({ personId, treeId }) => {
                const formApi = formRef.current;
                invariant(formApi, "Form ref is not initialized");
                await applyPersonToForm({
                    personId,
                    treeId,
                    queryClient,
                    formApi,
                    formMetadata,
                    defaultFormValues,
                    pageContext,
                    isEditForm,
                });
            },
        }),
        [queryClient, formMetadata, defaultFormValues, pageContext, isEditForm],
    );

    useOnAddField(({ fieldName, type, newItemIndex, mappedAssertion }) => {
        const formApi = formRef.current;
        invariant(formApi, "Form ref is not initialized");
        const matchAllTermsExactly = matchAllTermsRef.current?.matchAllTermsExactly ?? false;
        const matchAllTermsSupported = matchAllTermsRef.current?.matchAllTermsSupported ?? false;
        const setValueAsExact = matchAllTermsSupported && matchAllTermsExactly;
        if (type === "event") {
            const input = createEventDefaultInstance(pageContext.runtimeConfig.defaultYearProximity);
            if (setValueAsExact) {
                input.dateProximity.year = 0;
                input.dateProximity.month = 0;
                input.dateProximity.day = 0;
                input.locationExactness = LOCATION_EXACTNESS_SCOPE.ThisPlace;
            }
            // @ts-expect-error TS can't resolve FieldValue for deeply nested array paths
            formRef.current.appendArrayItem(fieldName as FieldPath<FormValues>, input);
        } else {
            const input = cloneDeep(RELATION_DEFAULT_INSTANCE);
            if (setValueAsExact) {
                input.givenNameExactness.isExact = true;
                input.surnameExactness.isExact = true;
            }
            formRef.current.appendArrayItem(fieldName as FieldPath<FormValues>, input);
        }
        const focusPath = `${fieldName}.${newItemIndex}.${mappedAssertion}` as FieldPath<FormValues>;
        requestAnimationFrame(() => {
            formApi.setFocus(focusPath);
        });
    });

    useOnRemoveField(({ fieldName, index }) => {
        const formApi = formRef.current;
        invariant(formApi, "Form ref is not initialized");
        formApi.removeArrayItem(fieldName as FieldPath<FormValues>, index);
    });

    return (
        <Form
            className={classNames(formInputsSize ? FORM_SIZE_MAP[formInputsSize] : "formLarge480", styles.container, {
                loading: !hydrated,
            })}
            ref={formRef}
            initialValues={initialFormValuesRef.current}
            onSubmit={onSubmit}
            data-sfs-hydrated={hydrated ? "true" : "false"}
            inert={hydrated ? undefined : ""}
            aria-busy={hydrated ? undefined : true}
        >
            <MatchAllTermsProvider ref={matchAllTermsRef}>
                <IsEditFormProvider value={isEditForm}>
                    <MemoizedFullFormStructure />
                </IsEditFormProvider>
            </MatchAllTermsProvider>
        </Form>
    );
};
